import {
  CalendarIcon,
  DocumentIcon,
  FolderIcon,
  UserGroupIcon,
  UserIcon,
} from "@heroicons/react/20/solid";
import { useUser } from "../../../contexts/UserContext";
import { OverlayOption } from "../types/OverlayOption";
import { useMatter } from "../../../contexts/MatterContext";

interface OverlayProps {
  type: "user" | "event" | "file" | "folder" | "slash";
  supportedTypes?: string[];
  supportedSlashOptions?: OverlayOption["option"][];
  query?: string;
  onSelect: (option: OverlayOption) => void;
}

const Overlay = ({
  query,
  type,
  onSelect,
  supportedSlashOptions = ["event", "participant", "file"],
}: OverlayProps) => {
  const { currentMatter } = useMatter();

  const options = (): OverlayOption[] => {
    switch (type) {
      case "slash": {
        return [
          { type: "option" as const, id: "participant", name: "Participant" },
          { type: "option" as const, id: "event", name: "Event" },
          { type: "option" as const, id: "file", name: "Evidence" },
        ].filter((option) => supportedSlashOptions.includes(option.id as any));
      }
      case "user":
        return (currentMatter?.users ?? []).map((user) => ({
          type: "user",
          id: user._id,
          name: user.name || `${user.firstName} ${user.lastName}`,
        }));
      default:
        return [];
    }
  };

  const results = options().filter((option) => {
    const name = option.name.toLowerCase();
    const searchQuery = query?.toLowerCase() ?? "";
    return name.includes(searchQuery);
  });

  const renderIcon = (option: OverlayOption) => {
    if (option.type === "user") return <UserIcon className="w-4 h-4 mr-2" />;
    if (option.id === "event") return <CalendarIcon className="w-4 h-4 mr-2" />;
    if (option.id === "file") return <DocumentIcon className="w-4 h-4 mr-2" />;
    if (option.id === "folder") return <FolderIcon className="w-4 h-4 mr-2" />;
    if (option.id === "participant")
      return <UserGroupIcon className="w-4 h-4 mr-2" />;
    return null;
  };

  return (
    <div className="overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 w-72 dark:bg-gray-800 dark:border-gray-700">
      {results.length === 0 && (
        <div className="p-3 text-sm text-gray-500 dark:text-gray-400">
          No results found
        </div>
      )}
      {results.map((option) => (
        <button
          key={option.id}
          onClick={() => onSelect(option)}
          className="flex items-center w-full px-4 py-2 text-left hover:bg-blue-50 dark:hover:bg-gray-700 focus:outline-none focus:bg-blue-100 dark:focus:bg-gray-600"
        >
          {renderIcon(option)}
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {option.name}
          </span>
        </button>
      ))}
    </div>
  );
};

export default Overlay;
